import DOMPurify from 'dompurify';

/**
 * Security-focused HTML sanitization utilities using DOMPurify
 */

/**
 * Sanitize HTML content for safe rendering in React components
 * 
 * @param html - The HTML string to sanitize
 * @param allowedTags - Optional array of allowed HTML tags (uses DOMPurify defaults if not provided)
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (html: string, allowedTags?: string[]): string => {
  const config = {
    // Allow common formatting tags while blocking dangerous ones
    ALLOWED_TAGS: allowedTags || [
      'p', 'br', 'strong', 'em', 'u', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'table', 'tr', 'td', 'th', 'thead', 'tbody'
    ],
    // Allow safe attributes
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height', 'style', 'class', 'id',
      'align', 'type', 'target'
    ],
    // Additional security measures
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    ADD_TAGS: [], // Don't add any custom tags
    ADD_ATTR: [], // Don't add any custom attributes
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'iframe'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    // Remove data URIs to prevent XSS
    FORBID_CONTENTS: ['script', 'style'],
    // Keep whitespace
    KEEP_CONTENT: true,
    // Return safe HTML
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
  };

  try {
    const sanitized = DOMPurify.sanitize(html, config);
    
    // Log sanitization for security monitoring in development
    if (import.meta.env.DEV && sanitized !== html) {
      console.warn('HTML content was sanitized:', {
        original: html.substring(0, 100) + (html.length > 100 ? '...' : ''),
        sanitized: sanitized.substring(0, 100) + (sanitized.length > 100 ? '...' : '')
      });
    }
    
    return sanitized;
  } catch (error) {
    console.error('HTML sanitization failed:', error);
    // Return empty string if sanitization fails
    return '';
  }
};

/**
 * Sanitize HTML specifically for email templates
 * More restrictive configuration for user-generated email content
 */
export const sanitizeEmailHtml = (html: string): string => {
  return sanitizeHtml(html, [
    'p', 'br', 'strong', 'em', 'u', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5',
    'ul', 'ol', 'li', 'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody'
  ]);
};

/**
 * Sanitize HTML for display in consent templates
 * Allows styling attributes needed for consent template formatting
 */
export const sanitizeConsentTemplateHtml = (html: string): string => {
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody'
    ],
    ALLOWED_ATTR: [
      'style', 'class', 'align', 'type'
    ],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'iframe'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
  };

  try {
    return DOMPurify.sanitize(html, config);
  } catch (error) {
    console.error('Consent template HTML sanitization failed:', error);
    return '';
  }
};